import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Entitlements } from "../../types/entitlements";
import type { OnboardingStatus } from "../../features/onboarding/types";

type Subscription = {
  status: "freemium" | "paid" | "free_trial";
  stripeCustomerId?: string;
  planId?: string;
  remainingCases?: number;
  request?: boolean;
};

export interface LawyerState {
  firstName: string;
  middleName: string;
  lastName: string;
  pronouns: string;
  title: string;
  email: string;
  address: string;
  state: string;
  city: string;
  zipcode: string;
  phone: string;
  practiceName: string;
  lawFirm: string;
  law_firm_id?: string | null;
  // Firm-level switch, off by default (Settings > Time & billing).
  time_tracking_enabled: boolean;
  // Classic drafting allowlist, set per firm by a superadmin.
  legacyDrafting?: boolean;
  lawFirmLogo?: string | null;
  loadingLogo?: boolean;
  website: string;
  avatarSrc: string;
  avatarName: string;
  uid: string;
  role: string;
  subscription?: Subscription;
  // Undefined means "not reported by the backend", which the UI treats as
  // unrestricted rather than blanking out features.
  entitlements?: Entitlements;
  created_at?: string;
  hasAcceptedTerms: boolean;
  hideQualityReview: boolean;
  betaFeatures?: string[];
  hiddenPrompts?: string[];
  exhibitListFormat?: string | null;
  // Attorney-wide defaults for the Upload & Organize tab.
  organizeInstruction?: string;
  documentTitleStyle?: string;
  // The model the Managed Deep Agents (research, drafting, PDF, autocategorize)
  // run on: "" = default, "inherit" = the case agent turn's model, else a
  // catalog model id. Set in Settings > Agents.
  subagentModel?: string;
  subagentThinking?: boolean;
  // The guided first run (`/onboarding`): `pending` until it is started; null
  // for members who were here before it existed.
  onboardingStatus?: OnboardingStatus | null;
}

export interface SubagentModelPick {
  model: string;
  thinking: boolean;
}

export interface UpdateLawyer {
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  lawFirm?: string;
  address: string;
  phone: string;
  state: string;
  city: string;
  zipcode: string;
}

const initialState: LawyerState = {
  firstName: "",
  middleName: "",
  lastName: "",
  pronouns: "",
  title: "",
  email: "",
  address: "",
  state: "",
  city: "",
  zipcode: "",
  phone: "",
  practiceName: "",
  lawFirm: "",
  time_tracking_enabled: false,
  lawFirmLogo: null,
  loadingLogo: true,
  website: "",
  avatarSrc: "",
  avatarName: "",
  uid: "",
  role: "",
  hasAcceptedTerms: false,
  hideQualityReview: false,
  entitlements: undefined,
  betaFeatures: [],
  hiddenPrompts: [],
  exhibitListFormat: null,
  organizeInstruction: "",
  documentTitleStyle: "",
  subagentModel: "",
  subagentThinking: false,
  onboardingStatus: null
};
export interface LawyerReducer {
  lawyer: LawyerState;
}

export interface UpdateAvatar {
  avatarSrc: string;
  avatarName: string;
}

export const lawyerSlice = createSlice({
  name: "lawyer",
  initialState,
  reducers: {
    setLoadingLogo: (state, action: PayloadAction<boolean>) => {
      state.loadingLogo = action.payload;
    },
    setLogo: (state, action: PayloadAction<null | string>) => {
      state.lawFirmLogo = action.payload;
    },
    setLawyerData: (state, action: PayloadAction<LawyerState>) => {
      const { lawFirmLogo, ...rest } = action.payload;
      Object.assign(state, rest);
    },
    resetLawyerData: (state) => {
      const { lawFirmLogo, loadingLogo, ...rest } = initialState;
      Object.assign(state, rest);
    },
    setLawyerUID: (state, action: PayloadAction<string>) => {
      state.uid = action.payload;
    },
    updateLawyerEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    updateLawyerAvatar: (state, action: PayloadAction<UpdateAvatar>) => {
      state.avatarSrc = action.payload.avatarSrc;
      state.avatarName = action.payload.avatarName;
    },
    updateLawyerData: (state, { payload }: PayloadAction<UpdateLawyer>) => {
      return {
        ...state,
        ...payload
      };
    },
    setOrganizeInstructionDefault: (state, action: PayloadAction<string>) => {
      state.organizeInstruction = action.payload;
    },
    setDocumentTitleStyleDefault: (state, action: PayloadAction<string>) => {
      state.documentTitleStyle = action.payload;
    },
    setSubagentModelPick: (state, action: PayloadAction<SubagentModelPick>) => {
      state.subagentModel = action.payload.model;
      state.subagentThinking = action.payload.thinking;
    },
    setTimeTrackingEnabled: (state, action: PayloadAction<boolean>) => {
      state.time_tracking_enabled = action.payload;
    },
    setOnboardingStatus: (
      state,
      action: PayloadAction<OnboardingStatus | null>
    ) => {
      state.onboardingStatus = action.payload;
    },
    // ---- Hidden Prompts Actions ----
    addHiddenPrompt: (state, action: PayloadAction<string>) => {
      if (!state.hiddenPrompts?.includes(action.payload)) {
        state.hiddenPrompts?.push(action.payload);
      }
    },
    removeHiddenPrompt: (state, action: PayloadAction<string>) => {
      state.hiddenPrompts =
        state.hiddenPrompts?.filter((id) => id !== action.payload) || [];
    },
    toggleHiddenPrompt: (state, action: PayloadAction<string>) => {
      if (state.hiddenPrompts?.includes(action.payload)) {
        state.hiddenPrompts = state.hiddenPrompts.filter(
          (id) => id !== action.payload
        );
      } else {
        state.hiddenPrompts?.push(action.payload);
      }
    }
  }
});

export const {
  setLawyerData,
  setLawyerUID,
  resetLawyerData,
  updateLawyerData,
  updateLawyerEmail,
  updateLawyerAvatar,
  setLogo,
  setLoadingLogo,
  setOrganizeInstructionDefault,
  setDocumentTitleStyleDefault,
  setSubagentModelPick,
  setTimeTrackingEnabled,
  setOnboardingStatus,
  addHiddenPrompt,
  removeHiddenPrompt,
  toggleHiddenPrompt
} = lawyerSlice.actions;

export default lawyerSlice.reducer;
